import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import api from '../../../data/api';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { ContentView } from '../utils/queryParamsKeys';
import { useVideoDetailsFromParams } from './useVideoDetailsFromParams';
import { useFetchClasses } from '../../../data/swrs/classes';

// === In-memory cache and expiry tracking ===
const cacheStore = new Map();
const cacheTimers = new Map();


const getRecommendedText = (type) => {
	switch (type) {
		case ContentView.CLASSES:
			return 'ALL CLASSES';
		case ContentView.PLAYVIEW:
		case '':
			return 'RECOMMENDED';
		default:
			return 'NEXT UP';
	}
};

// === Format video object ===
const mapVideoData = (video) => ({
	_id: video?._id || video?.videoId?._id || '',
	v_id: video?.v_id || video?.videoId?.v_id || '',
	thumnailLink: video?.thumnailLink || video?.videoId?.thumnailLink || '',
	videolink: video?.videolink || video?.videoId?.videolink || '',
	videoDetails: {
		duration: video?.duration ?? video?.videoId?.duration ?? '0:00'
	},
	title: video?.title || '',
	instructor: video?.instructor || '',
	tagsDetails: video?.tagsDetails || video?.tagsData || [],
	description: video?.description || '',
	duration: video?.duration ?? video?.videoId?.duration ?? '0:00'
});

const cacheTimeout = 9000
const useRecommendedVideos = ({skip=false, skipClasses=false}={}) => {
	const [videos, setVideos] = useState([]);
	const { typeOfContentView, playlistId, videoId } = useVideoDetailsFromParams();
	
	const userData = useAtomValue(userInfoAtom);
	const { isSuccess, videos: classVideos, isLoading, error, message } = useFetchClasses(skip || skipClasses ? null : userData?.token);

	const isRecommended = !typeOfContentView || typeOfContentView === ContentView.PLAYVIEW;
	const recommendedText = getRecommendedText(typeOfContentView);
	

	useEffect(() => {
		if (skip) return;
		if (!videoId) return;

		const controller = new AbortController();
		const { signal } = controller;

		const cacheKey = JSON.stringify({ typeOfContentView, playlistId, videoId });
		const now = Date.now();
		const cached = cacheStore.get(cacheKey);

		const isClassesView = typeOfContentView === ContentView.CLASSES;
		const noVideosAvailable = !isSuccess || isLoading || classVideos.length === 0;

		
		if (
			cached &&
			now - cached.timestamp < cacheTimeout && !(isClassesView && noVideosAvailable)
		) {
			setVideos(cached.data);
			return;
		}

		const fetchRecommended = async () => {
			try {
				let response, mapped = [];

				switch (typeOfContentView) {
					case ContentView.STUDIO:
						
						if (playlistId) {
							response = await api.getRecommendedStudioVideoData(videoId, playlistId, { signal });
							mapped = response?.data?.playlistWithVideos?.nextUpVideos?.map(mapVideoData) || [];
						}
						break;

					case ContentView.FEATURED:
						
						if (playlistId) {
							response = await api.getRecommendedFeatureVideoData(videoId, playlistId, { signal });
							mapped = response?.data?.existingVideos?.videos?.map(mapVideoData) || [];
						}
						break;

					case ContentView.PERSONAL:
						
						if (playlistId) {
							response = await api.getRecommendedPersonalVideoData(videoId, playlistId, { signal });
							mapped = response?.data?.existingVideos?.videos?.map(mapVideoData) || [];
							
						}
						break;

					case ContentView.CLASSES:
						
						if (!isLoading && isSuccess) {
							mapped = classVideos.map(mapVideoData);
						} else if (error) {
							console.error('Class video fetch error:', message);
						}
						break;

					default:
						
						response = await api.getRecommendedVideoData(videoId, { signal });
						mapped = response?.data?.map(mapVideoData) || [];
						break;
				}

				
				if (
					!isClassesView ||
					(isSuccess && classVideos.length > 0)
				) {
					cacheStore.set(cacheKey, { data: mapped, timestamp: Date.now() });

					if (cacheTimers.has(cacheKey)) clearTimeout(cacheTimers.get(cacheKey));

					const timer = setTimeout(() => {
						cacheStore.delete(cacheKey);
						cacheTimers.delete(cacheKey);
					}, 5000);

					cacheTimers.set(cacheKey, timer);
				}

				setVideos(mapped);
			} catch (err) {
				if (!signal.aborted) {
					console.error('Fetch failed:', err);
					toast.error('Failed to load recommended videos. Please try again later.');
				}
			}
		};

		fetchRecommended();
		return () => controller.abort();
	}, [videoId, typeOfContentView, playlistId, isSuccess, isLoading, error, classVideos]);

	return { videos, isRecommended, recommendedText };
};

export default useRecommendedVideos;
